import React, { useEffect, useRef } from 'react';

const STORAGE_KEY = 'adult-age-confirmed-v1';

export default function AgeGate({ children }) {
    const enterButton = useRef(null);
    const gate = useRef(null);
    const content = useRef(null);

    useEffect(() => {
        if (document.documentElement.getAttribute('data-adult-age-confirmed') === 'yes') return;
        try {
            if (window.localStorage.getItem(STORAGE_KEY) === 'yes') {
                document.documentElement.setAttribute('data-adult-age-confirmed', 'yes');
                return;
            }
        } catch {
            // The confirmation still works when browser storage is unavailable.
        }
        enterButton.current?.focus({ preventScroll: true });
        const keepFocus = event => {
            if (document.documentElement.getAttribute('data-adult-age-confirmed') === 'yes') return;
            if (event.key === 'Escape') event.preventDefault();
            if (event.key !== 'Tab') return;
            const controls = gate.current?.querySelectorAll('button, a[href]');
            if (!controls?.length) return;
            const first = controls[0], last = controls[controls.length - 1];
            if ((event.shiftKey && document.activeElement === first) ||
                (!event.shiftKey && document.activeElement === last) ||
                !gate.current.contains(document.activeElement)) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            }
        };
        document.addEventListener('keydown', keepFocus);
        return () => document.removeEventListener('keydown', keepFocus);
    }, []);

    function confirmAge() {
        try {
            window.localStorage.setItem(STORAGE_KEY, 'yes');
        } catch {
            // Keep the current visit usable without browser storage.
        }
        document.documentElement.setAttribute('data-adult-age-confirmed', 'yes');
        const target = content.current?.querySelector('main, h1');
        if (target) {
            const previousTabIndex = target.getAttribute('tabindex');
            target.setAttribute('tabindex', '-1');
            target.focus({ preventScroll: true });
            if (previousTabIndex === null) target.removeAttribute('tabindex');
            else target.setAttribute('tabindex', previousTabIndex);
        }
    }

    return (
        <>
            <style>{`
                html:not([data-adult-age-confirmed=yes]) { overflow: hidden; }
                .adult-age-content { display: contents; }

                .adult-age-gate { box-sizing: border-box; position: fixed; inset: 0; z-index: 2147483647; display: grid; place-items: center; overflow-y: auto; overscroll-behavior: contain; padding: 24px; background: rgba(10, 10, 18, .72); color: #f8f8fc; font-family: system-ui, sans-serif; text-align: center; }
                .adult-age-card { box-sizing: border-box; width: 100%; max-width: 460px; padding: clamp(24px, 6vw, 40px); background: #1b1b28; border: 1px solid #454559; border-radius: 24px; overflow-wrap: anywhere; }
                .adult-age-badge { display: inline-grid; place-items: center; width: 80px; height: 80px; border: 2px solid #f68fbe; border-radius: 50%; color: #f68fbe; font-size: 30px; font-weight: 800; }
                .adult-age-card h2 { margin: 24px 0 16px; color: #f8f8fc; font-size: clamp(26px, 6vw, 34px); line-height: 1.2; }
                .adult-age-card p { margin: 0 0 16px; color: #d0d0de; font-size: 16px; line-height: 1.6; }
                .adult-age-card button, .adult-age-card a { box-sizing: border-box; display: block; width: 100%; padding: 14px 16px; border-radius: 12px; font: 600 16px/1.5 system-ui, sans-serif; white-space: normal; text-decoration: none; cursor: pointer; }
                .adult-age-card button { margin-top: 24px; border: 1px solid #f68fbe; background: #f68fbe; color: #201020; }
                .adult-age-card a { margin-top: 12px; border: 1px solid #77778b; background: transparent; color: #f8f8fc; }
                .adult-age-card button:hover { background: #ffb4d4; }
                .adult-age-card a:hover { background: #303044; }
                .adult-age-card button:focus-visible, .adult-age-card a:focus-visible { outline: 3px solid #fff; outline-offset: 4px; }
                .adult-age-card noscript { display: block; margin-top: 20px; color: #d0d0de; font-size: 14px; line-height: 1.5; }
                html[data-adult-age-confirmed=yes] .adult-age-gate { display: none; }
            `}</style>
            <section ref={gate} className="adult-age-gate" role="dialog" aria-modal="true" aria-labelledby="adult-age-title" aria-describedby="adult-age-description adult-age-confirmation" lang="en">
                <div className="adult-age-card">
                    <div className="adult-age-badge" aria-hidden="true">18+</div>
                    <h2 id="adult-age-title">Adults only</h2>
                    <p id="adult-age-description">This website contains adult content and is intended only for people aged 18 and over.</p>
                    <p id="adult-age-confirmation">By entering, you confirm that you are at least 18 years old.</p>
                    <button ref={enterButton} type="button" onClick={confirmAge}>I am 18 or older — Enter</button>
                    <a href="https://www.google.com/" rel="noreferrer">I am under 18 — Leave</a>
                    <noscript>JavaScript is required to confirm your age and enter this website.</noscript>
                </div>
            </section>
            <div ref={content} className="adult-age-content">{children}</div>
        </>
    );
}
